import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';
const STORAGE_KEY = 'weatherDashboard_locations';
const DEFAULT_LOCATION = 'Ashburn, VA, US';

// Load locations from localStorage
const loadLocations = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Error loading locations from localStorage:', e);
  }
  return [DEFAULT_LOCATION];
};

// Save locations to localStorage
const saveLocations = (locations) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(locations));
  } catch (e) {
    console.error('Error saving locations to localStorage:', e);
  }
};

export const useWeather = (initialLocations = null) => {
  // Use provided initial locations, or load from storage
  const [locations, setLocations] = useState(() => {
    if (initialLocations && initialLocations.length > 0) {
      return initialLocations;
    }
    return loadLocations();
  });
  
  const [activeLocation, setActiveLocation] = useState(null);
  const [weatherData, setWeatherData] = useState({});
  const [alertsData, setAlertsData] = useState({});
  const [historyData, setHistoryData] = useState({});
  const [aqiData, setAqiData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Save locations whenever they change
  useEffect(() => {
    saveLocations(locations);
  }, [locations]);

  // Set initial active location
  useEffect(() => {
    if (!activeLocation && locations.length > 0) {
      setActiveLocation(locations[0]);
    } else if (activeLocation && !locations.includes(activeLocation)) {
      setActiveLocation(locations[0] || null);
    }
  }, [locations, activeLocation]);

  const fetchHistoryForLocation = useCallback(async (location, days = 2) => {
    try {
      const res = await fetch(
        `${API_URL}/api/weather/history/${encodeURIComponent(location)}?days=${days}`
      );
      if (!res.ok) return null;
      const data = await res.json();
      setHistoryData(prev => ({
        ...prev,
        [`${location}:${days}`]: data
      }));
      return data;
    } catch (err) {
      console.error(`[useWeather] Error fetching history for "${location}":`, err.message);
      return null;
    }
  }, []);

  const fetchAqiForLocation = useCallback(async (location) => {
    try {
      const res = await fetch(
        `${API_URL}/api/weather/aqi/${encodeURIComponent(location)}`
      );
      if (!res.ok) return null;
      const data = await res.json();
      setAqiData(prev => ({ ...prev, [location]: data }));
      return data;
    } catch (err) {
      console.error(`[useWeather] Error fetching AQI for "${location}":`, err.message);
      return null;
    }
  }, []);

  const fetchAlertsForLocation = useCallback(async (location) => {
    try {
      const res = await fetch(
        `${API_URL}/api/weather/alerts/${encodeURIComponent(location)}`
      );
      if (!res.ok) return [];
      const data = await res.json();
      return data.alerts || [];
    } catch (err) {
      console.error(`[useWeather] Error fetching alerts for "${location}":`, err.message);
      return [];
    }
  }, []);

  const fetchWeatherForLocation = useCallback(async (location) => {
    try {
      const urls = {
        current: `${API_URL}/api/weather/current/${encodeURIComponent(location)}`,
        forecast: `${API_URL}/api/weather/forecast/${encodeURIComponent(location)}`,
        hourly: `${API_URL}/api/weather/hourly/${encodeURIComponent(location)}`,
      };

      const [currentRes, forecastRes, hourlyRes] = await Promise.all([
        fetch(urls.current),
        fetch(urls.forecast),
        fetch(urls.hourly),
      ]);

      if (!currentRes.ok) {
        throw new Error(`Failed to fetch weather for ${location}: HTTP ${currentRes.status}`);
      }

      const current = await currentRes.json();
      const forecast = forecastRes.ok ? await forecastRes.json() : null;
      const hourly = hourlyRes.ok ? await hourlyRes.json() : null;

      return { current, forecast, hourly, location };
    } catch (err) {
      console.error(`[useWeather] Error fetching "${location}":`, err.message);
      return null;
    }
  }, []);

  const fetchAllWeather = useCallback(async () => {
    if (locations.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const [weatherResults, alertsResults, aqiResults] = await Promise.all([
        Promise.all(locations.map(loc => fetchWeatherForLocation(loc))),
        Promise.all(locations.map(loc => fetchAlertsForLocation(loc))),
        Promise.all(locations.map(loc => fetchAqiForLocation(loc))),
      ]);

      const newWeatherData = {};
      weatherResults.forEach(result => {
        if (result) {
          newWeatherData[result.location] = result;
        }
      });

      const newAlertsData = {};
      locations.forEach((loc, index) => {
        newAlertsData[loc] = alertsResults[index] || [];
      });

      const newAqiData = {};
      locations.forEach((loc, index) => {
        if (aqiResults[index]) {
          newAqiData[loc] = aqiResults[index];
        }
      });

      setWeatherData(newWeatherData);
      setAlertsData(newAlertsData);
      setAqiData(newAqiData);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to fetch weather data');
    } finally {
      setLoading(false);
    }
  }, [locations, fetchWeatherForLocation, fetchAlertsForLocation, fetchAqiForLocation]);

  const addLocation = useCallback(async (location) => {
    // Check if location already exists
    if (locations.some(loc => loc.toLowerCase() === location.toLowerCase())) {
      return { success: false, error: 'Location already exists' };
    }

    // Validate the location by fetching weather
    setLoading(true);
    try {
      const result = await fetchWeatherForLocation(location);
      
      if (result && result.current) {
        // Use the city name from API response for consistency
        const normalizedLocation = `${result.current.city}, ${result.current.country}`;
        
        // Check again with normalized name
        if (locations.some(loc => loc.toLowerCase() === normalizedLocation.toLowerCase())) {
          setLoading(false);
          return { success: false, error: 'Location already exists' };
        }

        // Fetch alerts for the new location too
        const locationAlerts = await fetchAlertsForLocation(location);

        setLocations(prev => [...prev, location]);
        setWeatherData(prev => ({
          ...prev,
          [location]: result
        }));
        setAlertsData(prev => ({
          ...prev,
          [location]: locationAlerts
        }));
        setActiveLocation(location);
        setLoading(false);
        return { success: true };
      } else {
        setLoading(false);
        return { success: false, error: 'Location not found' };
      }
    } catch (err) {
      setLoading(false);
      return { success: false, error: 'Failed to validate location' };
    }
  }, [locations, fetchWeatherForLocation, fetchAlertsForLocation]);

  const removeLocation = useCallback((location) => {
    if (locations.length <= 1) {
      return { success: false, error: 'Cannot remove the last location' };
    }

    setLocations(prev => prev.filter(l => l !== location));
    setWeatherData(prev => {
      const newData = { ...prev };
      delete newData[location];
      return newData;
    });
    setAlertsData(prev => {
      const newData = { ...prev };
      delete newData[location];
      return newData;
    });

    if (activeLocation === location) {
      const remaining = locations.filter(l => l !== location);
      setActiveLocation(remaining[0] || null);
    }

    return { success: true };
  }, [locations, activeLocation]);

  const reorderLocations = useCallback((fromIndex, toIndex) => {
    setLocations(prev => {
      const newLocations = [...prev];
      const [removed] = newLocations.splice(fromIndex, 1);
      newLocations.splice(toIndex, 0, removed);
      return newLocations;
    });
  }, []);

  const clearAllLocations = useCallback(() => {
    setLocations([DEFAULT_LOCATION]);
    setWeatherData({});
    setAlertsData({});
    setActiveLocation(DEFAULT_LOCATION);
  }, []);

  const refresh = useCallback(() => {
    fetchAllWeather();
  }, [fetchAllWeather]);

  // Initial fetch and auto-refresh
  useEffect(() => {
    fetchAllWeather();
    const interval = setInterval(fetchAllWeather, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAllWeather]);

  return {
    locations,
    activeLocation,
    setActiveLocation,
    weatherData,
    alertsData,
    historyData,
    aqiData,
    loading,
    error,
    lastUpdated,
    addLocation,
    removeLocation,
    reorderLocations,
    clearAllLocations,
    refresh,
    fetchHistoryForLocation
  };
};

// Export a function to get saved locations (for TV display)
export const getSavedLocations = () => {
  return loadLocations();
};
